import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

interface ConnectCareFeatureFlag {
    enabled: boolean
    findTalent: {
        enabled: boolean
        beta?: boolean
        disabledLabel: string | null
        disabledMessageTitle: string | null
        disabledMessageDescription: string | null
    }
    jobBoard: {
        enabled: boolean
        beta?: boolean
        maxPostsPerOrganisation: number
        disabledLabel: string | null
        disabledMessageTitle: string | null
        disabledMessageDescription: string | null
    }
    jobApplicants?: {
        beta?: boolean
    }
    credits: {
        enabled: boolean
        beta?: boolean
        disabledLabel: string | null
        disabledMessageTitle: string | null
        disabledMessageDescription: string | null
    }
}

interface ConnectTalentFeatureFlag {
    enabled: boolean
    beta?: boolean
    myLearning: {
        enabled: boolean
        disabledLabel: string | null
        disabledMessageTitle: string | null
        disabledMessageDescription: string | null
    }
    jobSearch: {
        enabled: boolean
        disabledLabel: string | null
        disabledMessageTitle: string | null
        disabledMessageDescription: string | null
    }
}
interface ConnectFeatureFlags {
    connectCare?: ConnectCareFeatureFlag
    connectTalent?: ConnectTalentFeatureFlag
}

export const useFeatureFlagsStore = defineStore('featureFlags', () => {
    const flags = ref<ConnectFeatureFlags | null>(null)

    // The disabled-state copy is backend-driven, but the backend may omit it
    // (or not serve the expanded flags block at all). Without a fallback the
    // coming-soon pages v-if every line away and render an empty card, so each
    // getter falls back to generic copy instead of null.
    const DEFAULT_DISABLED_LABEL = 'Coming Soon'
    const DEFAULT_DISABLED_TITLE = 'This feature is on the way'
    const DEFAULT_DISABLED_DESCRIPTION =
        'We are putting the finishing touches on it. Check back soon to be among the first to try it out.'

    // Treats empty strings as absent too, so a blank backend value still renders copy.
    const withFallback = (value: string | null | undefined, fallback: string) =>
        value?.trim() ? value : fallback

    // Care Hub in Connect
    const isCareHubEnabled = computed(() => flags.value?.connectCare?.enabled ?? false)

    const isFindTalentEnabled = computed(() => flags.value?.connectCare?.findTalent?.enabled ?? false)
    const findTalentDisabledLabel = computed(() => withFallback(flags.value?.connectCare?.findTalent?.disabledLabel, DEFAULT_DISABLED_LABEL))
    const findTalentDisabledMessageTitle = computed(() => withFallback(flags.value?.connectCare?.findTalent?.disabledMessageTitle, DEFAULT_DISABLED_TITLE))
    const findTalentDisabledMessageDescription = computed(() => withFallback(flags.value?.connectCare?.findTalent?.disabledMessageDescription, DEFAULT_DISABLED_DESCRIPTION))

    // Talent Hub in Connect
    const isTalentHubEnabled = computed(() => flags.value?.connectTalent?.enabled ?? false)

    const isMyLearningEnabled = computed(() => flags.value?.connectTalent?.myLearning?.enabled ?? false)
    const myLearningDisabledLabel = computed(() => withFallback(flags.value?.connectTalent?.myLearning?.disabledLabel, DEFAULT_DISABLED_LABEL))
    const myLearningDisabledMessageTitle = computed(() => withFallback(flags.value?.connectTalent?.myLearning?.disabledMessageTitle, DEFAULT_DISABLED_TITLE))
    const myLearningDisabledMessageDescription = computed(() => withFallback(flags.value?.connectTalent?.myLearning?.disabledMessageDescription, DEFAULT_DISABLED_DESCRIPTION))

    function setFlags(featureFlags: ConnectFeatureFlags | undefined) {
        flags.value = featureFlags ?? null
    }

    // Job Board — split per side: the Care Hub owner board lives under
    // connectCare.jobBoard, the Talent Hub search under connectTalent.jobSearch.
    const isCareJobBoardEnabled = computed(() => flags.value?.connectCare?.jobBoard?.enabled ?? false)
    const isTalentJobBoardEnabled = computed(() => flags.value?.connectTalent?.jobSearch?.enabled ?? false)

    // Credit packages (Care Hub monetisation) — gates the Plan & Pricing page.
    const isCareCreditsEnabled = computed(() => flags.value?.connectCare?.credits?.enabled ?? false)

    // Beta labels — backend-driven, absent means no label.
    const isFindTalentBeta = computed(() => flags.value?.connectCare?.findTalent?.beta ?? false)
    const isCareJobBoardBeta = computed(() => flags.value?.connectCare?.jobBoard?.beta ?? false)
    const isCareJobApplicantsBeta = computed(() => flags.value?.connectCare?.jobApplicants?.beta ?? false)
    const isCareCreditsBeta = computed(() => flags.value?.connectCare?.credits?.beta ?? false)
    const isTalentHubBeta = computed(() => flags.value?.connectTalent?.beta ?? false)

    function reset() {
        flags.value = null
    }

    return {
        flags,
        isCareJobBoardEnabled,
        isTalentJobBoardEnabled,
        isCareCreditsEnabled,
        isFindTalentBeta,
        isCareJobBoardBeta,
        isCareJobApplicantsBeta,
        isCareCreditsBeta,
        isTalentHubBeta,

        // Care Hub in Connect
        isCareHubEnabled,
        isFindTalentEnabled,
        findTalentDisabledLabel,
        findTalentDisabledMessageTitle,
        findTalentDisabledMessageDescription,

        // Talent Hub in Connect
        isTalentHubEnabled,
        isMyLearningEnabled,
        myLearningDisabledLabel,
        myLearningDisabledMessageTitle,
        myLearningDisabledMessageDescription,

        setFlags,
        reset
    }
})
